import { useFilteredTracks } from "@common/hooks/livekit/useFilteredTracks"
import FocusLayoutM from "@common/livekit/components-web/FocusLayoutM"
import { GridLayoutM } from "@common/livekit/components-web/GridLayoutM"
import { ParticipantTileM } from "@common/livekit/components-web/ParticipantTileM"
import { getTrackRefId } from "@common/livekit/liveKitUtils"
import { useTracks, useVisualStableUpdate } from "@livekit/components-react"
import { Track } from "livekit-client"
import React, { useEffect, useMemo, useState } from "react"
import { Pressable, View } from "react-native"
import { connect } from "react-redux"
import { apiIdle, apiRequest } from "../../data/redux/actions/api"
import IconButtonM from "../components/button/IconButtonM"
import { theme } from "../theme/theme"
import InformationBarComponent from "./components/InformationBarComponent"

const RoomView = (props) => {
    const {
        participants,
        pinnedMode = false,
        setPinnedMode,
        miniMode,
        setMiniMode,
        contentWidth,
        currentUser,
        tutor,
        focusedMode,
        setFocusedMode,
        isHandRaised,
        isSoundMode
    } = props

    const tracks = useTracks(
        [
            { source: Track.Source.Camera, withPlaceholder: true },
            { source: Track.Source.ScreenShare, withPlaceholder: false },
        ],
        { onlySubscribed: false }
    )

    const stableTracks = useVisualStableUpdate(tracks, 6)

    const liveSession = props.currentLiveSession

    const filteredStableTracks = useFilteredTracks(
        stableTracks,
        currentUser,
        participants,
        liveSession
    )

    const [selectedTrackSid, setSelectedTrackSid] = useState(null)

    const noDisplayName = miniMode && !focusedMode

    // Stable keys derived from filteredStableTracks so the effect dep array
    // doesn't recompute reduce/map on every render.
    const filteredTracksKey = useMemo(
        () => filteredStableTracks?.reduce((acc, x) => acc + getTrackRefId(x), "") ?? "",
        [filteredStableTracks]
    )
    const selectedTrackStillPresent = useMemo(
        () =>
            filteredStableTracks
                ?.map((x) => getTrackRefId(x))
                ?.includes(selectedTrackSid) ?? false,
        [filteredStableTracks, selectedTrackSid]
    )

    useEffect(() => {
        const selectedTrackNotFound =
            filteredStableTracks?.find(
                (track) => getTrackRefId(track) === selectedTrackSid
            ) === undefined

        const myself =
            filteredStableTracks?.find(
                (track) =>
                    track?.participant?.identity?.split(".")[0] ===
                    currentUser?.id?.toString()
            ) ?? null

        if (
            (filteredStableTracks.length > 1 &&
                (!selectedTrackSid ||
                    getTrackRefId(myself) === selectedTrackSid)) ||
            selectedTrackNotFound
        ) {
            const notMyself =
                filteredStableTracks?.find(
                    (track) =>
                        track?.participant?.identity?.split(".")[0] !==
                        currentUser?.id?.toString()
                ) ?? null
            if (notMyself) {
                setSelectedTrackSid(getTrackRefId(notMyself))
            } else {
                setSelectedTrackSid(getTrackRefId(filteredStableTracks[0]))
            }
        } else if (
            filteredStableTracks.length > 0 &&
            (!selectedTrackSid || selectedTrackNotFound)
        ) {
            setSelectedTrackSid(getTrackRefId(filteredStableTracks[0]))
        }
    }, [filteredTracksKey, miniMode, selectedTrackStillPresent])

    const screenShareTrack = filteredStableTracks?.find(
        (track) => track.source === Track.Source.ScreenShare
    )

    // Synchronously compute the active stage track:
    // In dialogue mode with screen share, always prioritize screen share
    // This avoids race conditions from relying solely on async useEffect
    const activeStageTrackSid = (isSoundMode && screenShareTrack)
        ? getTrackRefId(screenShareTrack)
        : selectedTrackSid

    useEffect(() => {
        if (screenShareTrack?.source && isSoundMode) {
            setSelectedTrackSid(getTrackRefId(screenShareTrack))
            if (miniMode) setMiniMode(false)
        } else if (screenShareTrack?.source && !isSoundMode) {
            // Concentre mode: ignore screen share, prefer tutor's camera track
            const otherRegularTrack = filteredStableTracks?.find(
                (track) =>
                    track.source !== Track.Source.ScreenShare &&
                    track?.participant?.identity?.split(".")[0] !== currentUser?.id?.toString()
            )
            const regularTrack = otherRegularTrack || filteredStableTracks?.find(
                (track) => track.source !== Track.Source.ScreenShare
            )
            if (regularTrack) setSelectedTrackSid(getTrackRefId(regularTrack))
        } else if (!screenShareTrack?.source) {
            // Screen share stopped
            if (!miniMode && isSoundMode) setMiniMode(true)
        }
    }, [screenShareTrack?.source, isSoundMode])

    // For silent mode students, exclude screen share tracks from display
    const displayTracks = !isSoundMode
        ? filteredStableTracks?.filter(t => t.source !== Track.Source.ScreenShare)
        : filteredStableTracks

    // Find current user's track for bottom-right overlay
    const currentUserTrack = filteredStableTracks?.find(
        (track) =>
            track?.participant?.identity?.split(".")[0] ===
            currentUser?.id?.toString()
    )

    return (
        <Pressable
            style={{
                flex: 1,
                height: "100%",
                width: "100%",
                alignItems: "center",
                justifyContent: "center",
            }}
            onPress={() => {
                setFocusedMode(!focusedMode)
            }}
        >
            {pinnedMode ? (
                <View
                    style={{
                        flex: 1,
                        height: "100%",
                        width: "100%",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <FocusLayoutM
                        participants={participants}
                        miniMode={miniMode}
                        user={currentUser}
                        tutor={tutor}
                        stableTracks={displayTracks}
                        selectedTrackSid={activeStageTrackSid}
                        setSelectedTrackSid={setSelectedTrackSid}
                        contentWidth={contentWidth}
                        noDisplayName={noDisplayName}
                    />
                    {currentUserTrack &&
                        filteredStableTracks.length > 1 && (
                            <View  style={{
                                position: "absolute",
                                bottom: 0,
                                right: 0,
                                zIndex: 999
                            }}>
                            <View
                                style={{
                                    width: miniMode ? 60 : 120,
                                    height: miniMode ? 80 : 160,
                                    zIndex: 10,
                                    elevation: 10,
                                    borderWidth: 1,
                                    borderColor: theme.colors.light,
                                    borderRadius: 15,
                                    overflow: "hidden",
                                }}
                            >
                                <ParticipantTileM
                                    trackRef={currentUserTrack}
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        resizeMode: "cover",
                                    }}
                                    participants={participants}
                                    miniMode={miniMode}
                                    noDisplayName
                                    noOutOfApp
                                />
                            </View>

                                {miniMode && isHandRaised && (
                                    <IconButtonM
                                        icon="hand-paper"
                                        iconColor={theme.colors.accent}
                                        containerColor={theme.colors.primary}
                                        size={20}
                                        disabled
                                        style={{
                                            position: "absolute",
                                            top: -18,
                                            left: -18,
                                            zIndex: 1000,
                                            opacity: 1,
                                            borderColor: theme.colors.light,
                                            borderWidth: 1,
                                        }}
                                    />
                                )}
                            </View>
                        )}
                </View>
            ) : (
                <GridLayoutM
                    tracks={displayTracks}
                    participants={participants}
                    onSelectTrack={(track) => {
                        setSelectedTrackSid(getTrackRefId(track))
                        setPinnedMode(true)
                    }}
                />
            )}
        </Pressable>
    )
}

const mapStateToProps = (state) => {
    return {
        currentLiveSession: state.data.currentUser?.object?.live_sessions?.find(
            (x) => x?.id === state.liveMode?.liveSessionId
        ),
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
}

export default connect(mapStateToProps, mapDispatchToProps)(RoomView)
